import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { t } from "i18next";
import { Box, Flex, Text, useToast } from "@chakra-ui/react";
import { useExportEmployee } from "hooks/useExport";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import CardHeader from "components/Card/CardHeader";
import PrimaryButton from "components/Buttons/PrimaryButton";
import SecondaryButton from "components/Buttons/SecondaryButton";
import {
  Tag,
  TagCloseButton,
  TagLabel,
  Input,
  Box as ChakraBox,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { useModalState } from "hooks/useModalState";
import ModalEmployees from "components/modals/ModalEmployees";
import useSearchEmployee from "hooks/useSearchEmployee";
import { useEmployeeStore } from "store/employeeStore";
import CourseEmployeeInfo from "views/Course/CourseEmployeeInfo";

const ExportExcelEmployee = () => {
  const [fiscalYears, setFiscalYears] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [inputError, setInputError] = useState("");
  const [employeeError, setEmployeeError] = useState("");
  const { fetchAllDataEmployees } = useSearchEmployee();

  const MAX_EMPLOYEES = 70;
  const MAX_YEARS = 10;
  const toast = useToast();

  const { modalState, open, close } = useModalState({
    modalEmployees: false,
  });

  const { onSubmit: onSearchEmployee } = useSearchEmployee(
    {
      is_required_all: false,
      is_open: modalState.modalEmployees,
    },
    null
  );
  const { resetAllEmployee } = useEmployeeStore();
  const selectedEmployeeIds = useEmployeeStore((state) => state.selectedEmployeeIds);

  const { mutateAsync: exportEmployee } = useExportEmployee();

  const reset = () => {
    setFiscalYears([]);
    setInputValue("");
    setInputError("");
    setEmployeeError("");
    resetAllEmployee();
  };

  useEffect(() => {
    fetchAllDataEmployees();
    reset();
  }, []);

  // Clear employee error when employees are selected
  useEffect(() => {
    const employeeCount = selectedEmployeeIds?.length || 0;
    if (employeeCount > 0) {
      setEmployeeError((prevError) => (prevError ? "" : prevError));
    }
  }, [selectedEmployeeIds?.length]);

  // On close: validate working list; if valid, commit back to selectedEmployeeIds and clear the working list
  // If over the employee limit, keep the modal open and show an error
  const closeModal = () => {
    close("modalEmployees");
  };

  const validateYear = (year) => {
    const yearRegex = /^\d{4}$/;
    return (
      yearRegex.test(year) && parseInt(year) >= 2000 && parseInt(year) <= 3000
    );
  };

  const handleInputKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " " || e.key === "Tab") {
      e.preventDefault();
      e.stopPropagation();
      const year = inputValue.trim();

      if (year) {
        if (fiscalYears.length >= MAX_YEARS) {
          setInputError(t("pages.export.error_max_years"));
          return;
        }
        if (validateYear(year)) {
          if (!fiscalYears.includes(year)) {
            setFiscalYears([...fiscalYears, year]);
            setInputValue("");
            setInputError("");
          } else {
            setInputError(t("pages.export.error_duplicate_year"));
          }
        } else {
          setInputError(t("pages.export.error_invalid_format"));
        }
      }
    }
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    if (inputError) {
      setInputError("");
    }
  };

  const removeFiscalYear = (yearToRemove) => {
    const next = fiscalYears.filter((year) => year !== yearToRemove);
    setFiscalYears(next);
    if (next.length < MAX_YEARS && inputError) {
      setInputError("");
    }
  };

  const handleExport = async () => {
    let hasError = false;

    // Require at least one fiscal year
    if (!fiscalYears.length) {
      setInputError(t("pages.export_required.error_required_years"));
      hasError = true;
    } else {
      setInputError("");
    }

    // Require at least one employee
    const committedCount = selectedEmployeeIds?.length || 0;
    if (!committedCount) {
      setEmployeeError(t("pages.export_required.error_required_employees"));
      hasError = true;
    } else {
      setEmployeeError("");
    }

    // If there are validation errors, don't proceed
    if (hasError) {
      return;
    }

    try {
      const years = fiscalYears;
      await exportEmployee({ employee_ids: selectedEmployeeIds, years });
      
      toast({
        status: "success",
        description: "エクスポート処理を開始しました。",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    } catch (error) {
      toast({
        status: "error",
        description: error.message || t("pages.messages.error"),
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    }
  };

  const handleCancel = () => {
    setFiscalYears([]);
    setInputValue("");
    setInputError("");
    setEmployeeError("");
    resetAllEmployee();
  };

  return (
    <Flex direction="column" pt={{ base: "120px", md: "40px" }}>
      <Card overflowX={"hidden"} pb="0px">
        <CardHeader p="6px 0px 20px 0px">
          <Flex justifyContent={"space-between"}>
            <Text fontSize="xl" fontWeight="bold">
              {t("pages.export.title")}
            </Text>
          </Flex>
        </CardHeader>
        <CardBody>
          <form>
            <CourseEmployeeInfo
              handleFirstBtn={() => open("modalEmployees")}
              textFirstBtn={t("pages.export.user_confirmation")}
              isSelectedAll={false}
              error={employeeError}
              isRequired={true}
            />

            <Flex gap={"10px"} align={"center"} mb={"10px"}>
              <Box w={"15%"}>
                <Text fontSize={"14px"}>
                  {t("pages.export.target_fiscal_year")}
                  <Text as="span" color="red.500"> *</Text>
                </Text>
              </Box>
              <Box width={"50%"} >
                <ChakraBox
                  border="1px solid"
                  borderColor={inputError ? "red.300" : "gray.300"}
                  borderRadius="md"
                  p={1}
                  minH="30px"
                >
                  <Wrap spacing={2} align="center">
                    {fiscalYears.map((year) => (
                      <WrapItem key={year}>
                        <Tag size="md" colorScheme="blue" borderRadius="full">
                          <TagLabel>{year}</TagLabel>
                          <TagCloseButton
                            onClick={() => removeFiscalYear(year)}
                          />
                        </Tag>
                      </WrapItem>
                    ))}
                    <WrapItem style={{ flex: 1 }}>
                      <Input
                        value={inputValue}
                        onChange={handleInputChange}
                        onKeyDown={handleInputKeyDown}
                        placeholder={
                          fiscalYears.length === 0
                            ? t("pages.export.fiscal_year_placeholder")
                            : ""
                        }
                        border="none"
                        outline="none"
                        _focus={{ boxShadow: "none" }}
                        fontSize="14px"
                      />
                    </WrapItem>
                  </Wrap>
                </ChakraBox>
                {inputError && (
                  <Text color="red.500" fontSize="12px" mt={1}>
                    {inputError}
                  </Text>
                )}
                <Text fontSize="12px" color="gray.600" mt={1}>
                  複数の値を入力する場合は、Enterを押してください
                </Text>
              </Box>
            </Flex>

            <Flex justifyContent={"end"} gap={"20px"} pb={"10px"}>
              <PrimaryButton
                text={t("pages.export.export_button")}
                onClick={handleExport}
              />
              <SecondaryButton
                text={t("pages.course_form.cancel")}
                onClick={handleCancel}
              />
            </Flex>
          </form>
        </CardBody>
      </Card>
      {modalState.modalEmployees && (
        <ModalEmployees
          isOpen={modalState.modalEmployees}
          onClose={closeModal}
          maxEmployee={MAX_EMPLOYEES}
          editMode={false}
          setValue={null}
          onSubmit={onSearchEmployee}
          isExportProcess={true}
        />
      )}
    </Flex>
  );
};

export default ExportExcelEmployee;
